import { useTranslation } from 'react-i18next';
import { Box, styled, useTheme } from '@mui/material';

import { ThemeWrapper, Typography } from '@zendesk/forethought-elements';
import { ColorMode } from 'src/components/app/types';

export const ExecutionIndicator = ({
  message,
  theme = 'light',
  timestamp,
}: {
  message: React.ReactNode;
  theme?: ColorMode;
  timestamp: string;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  const { palette } = useTheme();

  if (theme === 'dark') {
    return <ConversationStarted message={message} timestamp={timestamp} />;
  }

  return (
    <Box
      sx={{
        alignSelf: 'center',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
      }}
    >
      <TextContainer>
        <Typography variant='font14Bold'>{message}</Typography>
        <Typography color={palette.colors.grey[900]} variant='font14Medium'>
          {t('common.timestampSeparator', { timestamp })}
        </Typography>
      </TextContainer>
      <Line />
    </Box>
  );
};

export const ConversationStarted = ({
  message,
  timestamp,
}: {
  message: React.ReactNode;
  timestamp: string;
}) => {
  const { t } = useTranslation('intent-conversation-analytics');
  return (
    <Box
      sx={{
        alignItems: 'center',
        alignSelf: 'center',
        display: 'flex',
        justifyContent: 'center',
        position: 'relative',
        width: '100%',
      }}
    >
      {/* Left line */}
      <Box
        sx={{
          backgroundColor: 'white',
          flex: 1,
          height: '1px',
          marginRight: '8px',
        }}
      />
      <Box
        sx={{
          alignItems: 'center',
          borderRadius: '4px',
          display: 'flex',
          px: '8px',
        }}
      >
        <ThemeWrapper themeVersion='DarkMode'>
          <Typography variant='font14Bold'>{message}</Typography>
          <Typography variant='font14Medium'>
            {t('common.timestampSeparator', { timestamp })}
          </Typography>
        </ThemeWrapper>
      </Box>
      {/* Right line */}
      <Box
        sx={{
          backgroundColor: 'white',
          flex: 1,
          height: '1px',
          marginLeft: '8px',
        }}
      />
    </Box>
  );
};

const TextContainer = styled('div')`
  z-index: 5;
  background-color: white;
  padding: 0 8px;
`;

const Line = styled('div')`
  z-index: 0;
  width: 100%;
  height: 1px;
  background-color: ${props => props.theme.palette.colors.slate[200]};
  position: absolute;
  top: 12px;
`;
